import * as React from 'react';
import { cn } from '@wayste/shadcn/lib/utils';
import { Button, type ButtonVariant } from './button';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ActionFooterSize = 'xs' | 'sm' | 'default' | 'lg';

export interface ActionFooterAction {
    /** Button label text. */
    label: string;
    /** Click handler. */
    onClick?: () => void;
    /** Disable the button. */
    disabled?: boolean;
    /** Override the slot's default button variant. */
    variant?: ButtonVariant;
}

export interface ActionFooterProps extends React.ComponentProps<'div'> {
    /**
     * Controls padding and button size.
     * - xs  — px-3 / py-2.5, sm buttons
     * - sm  — px-4 / py-3,   sm buttons
     * - default — px-5 / py-4, default buttons
     * - lg  — px-6 / py-5,   default buttons
     * @default 'default'
     */
    size?: ActionFooterSize;
    /**
     * Primary action — renders on the far right as a filled primary button.
     */
    primaryAction?: ActionFooterAction;
    /**
     * Secondary action — renders to the left of the primary button.
     */
    secondaryAction?: ActionFooterAction;
    /**
     * Tertiary action — renders on the far left as an outlined button.
     */
    tertiaryAction?: ActionFooterAction;
    /**
     * Remove the top border. Useful when nesting inside a component that
     * already manages its own borders.
     * @default false
     */
    noBorder?: boolean;
}

// ─── Padding & button-size maps ───────────────────────────────────────────────

const paddingMap: Record<ActionFooterSize, string> = {
    xs: 'px-3 py-2.5',
    sm: 'px-4 py-3',
    default: 'px-5 py-4',
    lg: 'px-6 py-5',
};

// lg and default card footers use default-sized buttons; sm and xs use small.
const buttonSizeMap: Record<ActionFooterSize, 'sm' | 'default'> = {
    xs: 'sm',
    sm: 'sm',
    default: 'default',
    lg: 'default',
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * A standardised action bar used at the bottom of cards, panels, drawers, and
 * modals. Renders up to three buttons:
 * - `tertiaryAction` — far left, outlined
 * - `secondaryAction` + `primaryAction` — far right, grouped
 *
 * Falls back to `children` when no action props are provided.
 *
 * @example
 * ```tsx
 * <ActionFooter
 *   size="default"
 *   tertiaryAction={{ label: 'Delete', onClick: handleDelete }}
 *   secondaryAction={{ label: 'Cancel', onClick: handleCancel }}
 *   primaryAction={{ label: 'Save', onClick: handleSave }}
 * />
 * ```
 */
function ActionFooter({
    size = 'default',
    primaryAction,
    secondaryAction,
    tertiaryAction,
    noBorder = false,
    className,
    children,
    ...props
}: ActionFooterProps) {
    const btnSize = buttonSizeMap[size];
    const hasActions = !!(primaryAction || secondaryAction || tertiaryAction);

    return (
        <div
            data-slot="action-footer"
            className={cn(
                'bg-surface-tertiary flex w-full items-center gap-3',
                !noBorder && 'border-border-default border-t',
                paddingMap[size],
                className,
            )}
            {...props}
        >
            {hasActions ? (
                <>
                    {/* Tertiary — anchored left */}
                    {tertiaryAction && (
                        <Button
                            variant={tertiaryAction.variant ?? 'outline'}
                            size={btnSize}
                            onClick={tertiaryAction.onClick}
                            disabled={tertiaryAction.disabled}
                        >
                            {tertiaryAction.label}
                        </Button>
                    )}

                    {/* Secondary + Primary — anchored right */}
                    {(primaryAction || secondaryAction) && (
                        <div className="flex flex-1 items-center justify-end gap-3">
                            {secondaryAction && (
                                <Button
                                    variant={secondaryAction.variant ?? 'secondary'}
                                    size={btnSize}
                                    onClick={secondaryAction.onClick}
                                    disabled={secondaryAction.disabled}
                                >
                                    {secondaryAction.label}
                                </Button>
                            )}
                            {primaryAction && (
                                <Button
                                    variant={primaryAction.variant ?? 'default'}
                                    size={btnSize}
                                    onClick={primaryAction.onClick}
                                    disabled={primaryAction.disabled}
                                >
                                    {primaryAction.label}
                                </Button>
                            )}
                        </div>
                    )}
                </>
            ) : (
                children
            )}
        </div>
    );
}

export { ActionFooter };
